import { createContext, ReactNode, useContext, useState } from 'react';
import { useTherapyContextUserQuery } from '../graphQL';
import { useNavigateToPatientPortal } from '../hooks/useNavigateToPatientPortal';

type NavigateToPatientPortalProps = {
  mrn?: string;
  onCompleted?: () => void;
  onError?: () => void;
};

type TherapyContextType = {
  hasLoaded: boolean;
  orgName: string;
  navigateToPatientPortal: (props?: NavigateToPatientPortalProps) => void;
  orgHasClinicalSelfReferrals: boolean;
};

const TherapyContext = createContext<TherapyContextType>({
  hasLoaded: false,
  orgName: '',
  navigateToPatientPortal: () => undefined,
  orgHasClinicalSelfReferrals: false,
});

export const TherapyConsumer = TherapyContext.Consumer;

export const TherapyProvider = ({ children }: { children: ReactNode }): JSX.Element => {
  const [hasLoaded, setHasLoaded] = useState(false);
  const [orgName, setOrgName] = useState<string>('');
  const [orgHasClinicalSelfReferrals, setOrgHasClinicalSelfReferrals] = useState(false);

  const { navigateToPatientPortal } = useNavigateToPatientPortal();

  useTherapyContextUserQuery({
    onCompleted: ({ onboardedHubUser }) => {
      setOrgName(onboardedHubUser?.organization?.name ?? '');
      setOrgHasClinicalSelfReferrals(
        onboardedHubUser?.organizationHasClinicalSelfReferrals ?? false,
      );
      setHasLoaded(true);
    },
  });

  const providerValue: TherapyContextType = {
    hasLoaded,
    orgName, // Temporary until therapy copy can come from the api.
    navigateToPatientPortal,
    orgHasClinicalSelfReferrals,
  };

  return <TherapyContext.Provider value={providerValue}>{children}</TherapyContext.Provider>;
};

export const useTherapyContext = (): TherapyContextType => {
  return useContext(TherapyContext);
};
